// Shared building blocks for the report pages: loading skeleton, error state with
// "Try again", the money-role check, KPI cards, date-range helpers and Excel / Print export.
import { useCallback, useEffect, useRef, useState, type ElementType, type ReactNode } from "react";
import { AlertTriangle, FileSpreadsheet, Loader2, Lock, Printer, RefreshCw } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/context/ToastContext";
import { useSettings } from "@/context/SettingsContext";
import { apiErrorMessage, type ApiError } from "@/lib/api";
import { formatCurrencyFull, localToday } from "@/lib/data";
import DateRangePicker from "../components/DateRangePicker";

/** Roles that may see revenue, cost and profit (same as the events list and dashboard). */
export const MONEY_ROLES = ["super_admin", "admin", "master"];

export function useCanSeeMoney(): boolean {
  const { user } = useAuth();
  return !!user && MONEY_ROLES.includes(user.role);
}

export const money = (n: number | string | null | undefined) => formatCurrencyFull(Number(n) || 0);
export const num = (v: unknown) => Number(v) || 0;
export const pct = (v: number | null | undefined, digits = 1) => `${(Number(v) || 0).toFixed(digits)}%`;

// ─── Dates ───────────────────────────────────────────────────────────────────

const pad = (n: number) => String(n).padStart(2, "0");
const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** First and last day of the month `offset` months from the current one. */
export function monthRange(offset = 0, base = localToday()): { from: string; to: string } {
  const [y, m] = base.split("-").map(Number);
  const first = new Date(y, m - 1 + offset, 1);
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
  return { from: iso(first), to: iso(last) };
}

export function yearRange(offset = 0, base = localToday()): { from: string; to: string } {
  const y = Number(base.slice(0, 4)) + offset;
  return { from: `${y}-01-01`, to: `${y}-12-31` };
}

/** Indian financial year (1 April – 31 March), `offset` years from the current one. */
export function fyRange(offset = 0, base = localToday()): { from: string; to: string } {
  const [y, m] = base.split("-").map(Number);
  const start = (m >= 4 ? y : y - 1) + offset;
  return { from: `${start}-04-01`, to: `${start + 1}-03-31` };
}

/** "FY 2026–27" for a financial year starting on `from`. */
export const fyLabel = (from: string) => {
  const y = Number(from.slice(0, 4));
  return `FY ${y}–${String(y + 1).slice(2)}`;
};

/** A quarter (Apr–Jun, Jul–Sep, Oct–Dec, Jan–Mar), `offset` quarters from the current one. */
export function quarterRange(offset = 0, base = localToday()): { from: string; to: string } {
  const [y, m] = base.split("-").map(Number);
  const first = new Date(y, (Math.floor((m - 1) / 3) + offset) * 3, 1);
  const last = new Date(first.getFullYear(), first.getMonth() + 3, 0);
  return { from: iso(first), to: iso(last) };
}

export type RangePreset = { label: string; range: () => { from: string; to: string } };

export const RANGE_PRESETS: RangePreset[] = [
  { label: "This month", range: () => monthRange(0) },
  { label: "Last month", range: () => monthRange(-1) },
  { label: "Next month", range: () => monthRange(1) },
  { label: "This quarter", range: () => quarterRange(0) },
  { label: "Last quarter", range: () => quarterRange(-1) },
  { label: "This financial year (Apr–Mar)", range: () => fyRange(0) },
  { label: "Last financial year", range: () => fyRange(-1) },
  { label: "This calendar year", range: () => yearRange(0) },
  { label: "Last calendar year", range: () => yearRange(-1) },
];

/** Human label for a range: the preset it matches, or the two dates. */
export function rangeLabel(from: string, to: string, presets: RangePreset[] = RANGE_PRESETS): string {
  const p = presets.find((x) => { const r = x.range(); return r.from === from && r.to === to; });
  const fmt = (d: string) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "");
  const dates = `${fmt(from)} – ${fmt(to)}`;
  return p ? `${p.label.replace(/ \(Apr–Mar\)$/, "")} (${dates})` : dates;
}

/**
 * A from/to pair edited by the date picker. `applied` only changes once both ends are
 * picked (the picker clears "to" while you choose "from"), so a half-picked range never loads.
 */
export function useRange(initial: { from: string; to: string }) {
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [applied, setApplied] = useState(initial);
  useEffect(() => {
    if (from && to && (from !== applied.from || to !== applied.to)) setApplied({ from, to });
  }, [from, to, applied.from, applied.to]);
  const set = useCallback((r: { from: string; to: string }) => { setFrom(r.from); setTo(r.to); }, []);
  return { from, to, setFrom, setTo, set, applied };
}

const ALL_DATES = "__all__";

/**
 * "Quick range" dropdown (this month, this quarter, this financial year, …) for any from/to
 * pair. It shows the preset the current dates match; `allowAll` adds "All dates" (both empty).
 */
export function PresetSelect({ from, to, onPick, presets = RANGE_PRESETS, allowAll = false, className = "" }: {
  from: string;
  to: string;
  onPick: (range: { from: string; to: string }) => void;
  presets?: RangePreset[];
  allowAll?: boolean;
  className?: string;
}) {
  const current = presets.find((x) => { const r = x.range(); return r.from === from && r.to === to; });
  const isAll = allowAll && !from && !to;
  return (
    <select
      aria-label="Quick range"
      value={isAll ? ALL_DATES : current?.label ?? ""}
      onChange={(e) => {
        if (e.target.value === ALL_DATES) { onPick({ from: "", to: "" }); return; }
        const p = presets.find((x) => x.label === e.target.value);
        if (p) onPick(p.range());
      }}
      className={`h-10 px-3 rounded-xl border border-stone-200 bg-white text-sm text-stone-600 outline-none focus:border-amber-400 ${className}`}
    >
      {!isAll && <option value="">{current ? "Quick range…" : "Custom dates"}</option>}
      {allowAll && <option value={ALL_DATES}>All dates</option>}
      {presets.map((p) => <option key={p.label} value={p.label}>{p.label}</option>)}
    </select>
  );
}

/** Date picker plus quick presets (this month, last month, this financial year, …). */
export function RangeControls({ range, presets = RANGE_PRESETS }: {
  range: ReturnType<typeof useRange>;
  presets?: RangePreset[];
}) {
  return (
    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
      <PresetSelect from={range.from} to={range.to} onPick={range.set} presets={presets} />
      <DateRangePicker fromDate={range.from} toDate={range.to} onFromChange={range.setFrom} onToChange={range.setTo} />
    </div>
  );
}

// ─── Loading ─────────────────────────────────────────────────────────────────

/**
 * Loads a report and tracks loading / error, with `retry` for the "Try again" button.
 * A failed load clears the data, so a page never shows ₹0 for a request that failed.
 */
export function useReport<T>(load: () => Promise<T>, deps: unknown[], enabled = true) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<number | null>(null);
  const [attempt, setAttempt] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!enabled) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    setError(null);
    setStatus(null);
    loadRef.current()
      .then((d) => { if (alive) setData(d); })
      .catch((e: unknown) => {
        if (!alive) return;
        setData(null);
        setStatus((e as ApiError)?.status ?? null);
        setError(apiErrorMessage(e, "Could not load this report."));
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, attempt, enabled]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { data, loading, error, status, retry };
}

export function ReportSkeleton({ cards = 4, blocks = 2 }: { cards?: number; blocks?: number }) {
  return (
    <div className="space-y-4 animate-pulse" aria-busy="true" aria-label="Loading report">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {Array.from({ length: cards }).map((_, i) => (
          <div key={i} className="h-24 rounded-xl bg-stone-100 border border-stone-200" />
        ))}
      </div>
      {Array.from({ length: blocks }).map((_, i) => (
        <div key={i} className="h-56 rounded-xl bg-stone-100 border border-stone-200" />
      ))}
    </div>
  );
}

export function ReportError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div role="alert" className="bg-white rounded-xl border border-red-200 p-6 flex flex-col items-center text-center gap-3">
      <div className="w-11 h-11 rounded-xl bg-red-50 flex items-center justify-center">
        <AlertTriangle className="w-5 h-5 text-red-600" />
      </div>
      <div>
        <p className="text-sm font-semibold text-stone-800">This report didn’t load</p>
        <p className="text-xs text-stone-500 mt-1 max-w-sm">{message}</p>
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="h-10 inline-flex items-center gap-2 px-4 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold"
      >
        <RefreshCw className="w-4 h-4" /> Try again
      </button>
    </div>
  );
}

export function NoMoneyAccess({ what = "this report" }: { what?: string }) {
  return (
    <div className="bg-white rounded-xl border border-stone-200 p-6 flex flex-col items-center text-center gap-3">
      <div className="w-11 h-11 rounded-xl bg-stone-100 flex items-center justify-center">
        <Lock className="w-5 h-5 text-stone-500" />
      </div>
      <p className="text-sm font-semibold text-stone-800">Only the owner and admins can see {what}</p>
      <p className="text-xs text-stone-500 max-w-sm">It shows revenue, costs and profit. Ask an admin if you need these figures.</p>
    </div>
  );
}

/** Loading → skeleton, failure → error with "Try again", 403 → no-access note, else the report. */
export function ReportBody<T>({ state, children, skeleton }: {
  state: { data: T | null; loading: boolean; error: string | null; status: number | null; retry: () => void };
  children: (data: T) => ReactNode;
  skeleton?: ReactNode;
}) {
  if (state.loading) return <>{skeleton ?? <ReportSkeleton />}</>;
  if (state.status === 403) return <NoMoneyAccess />;
  if (state.error || !state.data) return <ReportError message={state.error ?? "No data came back."} onRetry={state.retry} />;
  return <>{children(state.data)}</>;
}

// ─── Layout pieces ───────────────────────────────────────────────────────────

export function ReportHeader({ icon: Icon, iconClass = "text-amber-600", title, subtitle, children }: {
  icon: ElementType;
  iconClass?: string;
  title: string;
  subtitle?: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
      <div className="flex items-start gap-2 min-w-0">
        <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${iconClass}`} />
        <div className="min-w-0">
          <h1 className="text-lg font-bold text-stone-800">{title}</h1>
          {subtitle && <p className="text-xs text-stone-500">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

const TONES = {
  stone: "bg-white border-stone-200",
  emerald: "bg-emerald-50 border-emerald-100",
  amber: "bg-amber-50 border-amber-100",
  blue: "bg-blue-50 border-blue-100",
  violet: "bg-violet-50 border-violet-100",
  red: "bg-red-50 border-red-100",
  indigo: "bg-indigo-50 border-indigo-100",
  teal: "bg-teal-50 border-teal-100",
} as const;
export type Tone = keyof typeof TONES;

export function Kpi({ label, value, sub, icon: Icon, tone = "stone", valueClass = "text-stone-800" }: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  icon?: ElementType;
  tone?: Tone;
  valueClass?: string;
}) {
  return (
    <div className={`rounded-xl border p-3 sm:p-4 min-w-0 ${TONES[tone]}`}>
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon className="w-3.5 h-3.5 text-stone-500 shrink-0" />}
        <p className="text-[11px] font-bold uppercase tracking-wide text-stone-500 truncate">{label}</p>
      </div>
      <p className={`text-lg sm:text-xl font-bold tabular-nums truncate ${valueClass}`}>{value}</p>
      {sub && <p className="text-xs text-stone-500 mt-0.5">{sub}</p>}
    </div>
  );
}

export function KpiGrid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{children}</div>;
}

/** A white card whose wide content scrolls sideways inside it on phones. */
export function Card({ title, subtitle, actions, children, flush = false }: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  flush?: boolean;
}) {
  return (
    <section className="bg-white rounded-xl border border-stone-200 min-w-0">
      {(title || actions) && (
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-4">
          <div className="min-w-0">
            {title && <h2 className="text-sm font-semibold text-stone-700">{title}</h2>}
            {subtitle && <p className="text-xs text-stone-500">{subtitle}</p>}
          </div>
          {actions}
        </div>
      )}
      <div className={flush ? "pt-3" : "p-4"}>{children}</div>
    </section>
  );
}

/** Horizontal scroll wrapper for tables. */
export function TableScroll({ children, minWidth = 640 }: { children: ReactNode; minWidth?: number }) {
  return (
    <div className="overflow-x-auto -mx-4 sm:mx-0">
      <div className="px-4 sm:px-0" style={{ minWidth }}>{children}</div>
    </div>
  );
}

export function Note({ children }: { children: ReactNode }) {
  return <p className="text-xs text-stone-500 leading-relaxed">{children}</p>;
}

export const th = "py-2 px-2 text-xs font-semibold text-stone-500 whitespace-nowrap";
export const td = "py-2.5 px-2 whitespace-nowrap";

// ─── Export: Excel and Print ─────────────────────────────────────────────────

export type Cell = string | number | null | undefined;

/** money → ₹ with paise; number → plain count; percent → 12.5 shown as 12.5%; date → YYYY-MM-DD shown as 22 Sep 2026 */
export type CellType = "text" | "money" | "number" | "percent" | "date";

export interface ExportColumn { label: string; type?: CellType }

export interface ExportTable {
  /** Sheet name in Excel and the heading on the printout */
  title: string;
  columns: ExportColumn[];
  rows: Cell[][];
  totals?: Cell[];
}

export interface ExportSpec {
  /** Report name, e.g. "Monthly Profit & Loss" */
  title: string;
  /** File name without date or extension, e.g. "profit-and-loss" */
  fileName: string;
  period?: { from: string; to: string };
  /** Headline figures shown first (the Summary sheet in Excel, boxes on the printout) */
  summary?: { label: string; value: Cell; type?: CellType }[];
  tables: ExportTable[];
  notes?: string[];
}

const EXCEL_FORMATS: Partial<Record<CellType, string>> = {
  money: "#,##0.00",
  number: "#,##0",
  percent: '0.0"%"',
};

const showDate = (v: string) => {
  const d = new Date(`${v.slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

/** A value as it reads on screen / paper. */
function showCell(v: Cell, type: CellType = "text"): string {
  if (v === null || v === undefined || v === "") return "";
  if (typeof v === "number") {
    if (type === "money") return v < 0 ? `−${money(-v)}` : money(v);
    if (type === "percent") return pct(v);
    return v.toLocaleString("en-IN", { maximumFractionDigits: 2 });
  }
  return type === "date" ? showDate(v) : v;
}

const generatedAt = () => new Date().toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });

const fileStamp = (spec: ExportSpec) =>
  spec.period ? `${spec.period.from}_to_${spec.period.to}` : localToday();

/** Excel workbook: a Summary sheet, then one sheet per table with its headings in row 1. */
async function downloadExcel(spec: ExportSpec, business: string) {
  const XLSX = await import("xlsx");
  const wb = XLSX.utils.book_new();
  const used = new Set<string>();
  const sheetName = (name: string) => {
    const base = name.replace(/[\\/?*[\]:]/g, " ").trim().slice(0, 31) || "Sheet";
    let n = base;
    for (let i = 2; used.has(n.toLowerCase()); i++) n = `${base.slice(0, 26)} (${i})`;
    used.add(n.toLowerCase());
    return n;
  };
  const setFormat = (ws: Record<string, any>, r: number, c: number, type?: CellType) => {
    const cell = ws[XLSX.utils.encode_cell({ r, c })];
    let z = type && EXCEL_FORMATS[type];
    if (type === "number" && cell && typeof cell.v === "number" && !Number.isInteger(cell.v)) z = "#,##0.00";
    if (cell && z && typeof cell.v === "number") cell.z = z;
  };

  // Summary sheet: what this is, which dates, and the headline figures
  const head: Cell[][] = [
    [spec.title],
    ...(business ? [[business]] : []),
    ...(spec.period ? [[`Period: ${rangeLabel(spec.period.from, spec.period.to)}`]] : []),
    [`Generated: ${generatedAt()}`],
    [],
  ];
  const summary = spec.summary ?? [];
  const notes = spec.notes ?? [];
  const summaryRows: Cell[][] = [
    ...head,
    ...summary.map((s) => [s.label, s.value ?? ""]),
    ...(notes.length ? [[], ...notes.map((n) => [n])] : []),
  ];
  const ws0 = XLSX.utils.aoa_to_sheet(summaryRows.map((r) => r.map((v) => v ?? "")));
  summary.forEach((s, i) => setFormat(ws0, head.length + i, 1, s.type));
  ws0["!cols"] = [{ wch: 38 }, { wch: 20 }];
  XLSX.utils.book_append_sheet(wb, ws0, sheetName("Summary"));

  for (const t of spec.tables) {
    const aoa: Cell[][] = [t.columns.map((c) => c.label), ...t.rows, ...(t.totals ? [t.totals] : [])];
    const ws = XLSX.utils.aoa_to_sheet(aoa.map((r) => r.map((v) => v ?? "")));
    for (let r = 1; r < aoa.length; r++) t.columns.forEach((c, ci) => setFormat(ws, r, ci, c.type));
    ws["!cols"] = t.columns.map((c, ci) => ({
      wch: Math.min(40, Math.max(c.label.length, ...aoa.slice(1).map((r) => showCell(r[ci], c.type).length)) + 2),
    }));
    XLSX.utils.book_append_sheet(wb, ws, sheetName(t.title));
  }

  XLSX.writeFile(wb, `${spec.fileName}_${fileStamp(spec)}.xlsx`);
}

const esc = (v: unknown) =>
  String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string));

/** A clean A4 printout (or "Save as PDF") of the report, printed from a hidden frame. */
function printReport(spec: ExportSpec, business: string) {
  const widest = Math.max(0, ...spec.tables.map((t) => t.columns.length));
  const wide = widest > 7;
  const fontSize = widest > 14 ? 8 : widest > 10 ? 9.5 : 11;
  const right = (type?: CellType) => type === "money" || type === "number" || type === "percent";
  const summary = (spec.summary ?? []).map((s) =>
    `<div class="kpi"><div class="kl">${esc(s.label)}</div><div class="kv">${esc(showCell(s.value, s.type))}</div></div>`).join("");
  const tables = spec.tables.map((t) => `
    <h2>${esc(t.title)}</h2>
    ${t.rows.length === 0 ? '<p class="empty">Nothing in this period.</p>' : `
    <table>
      <thead><tr>${t.columns.map((c) => `<th class="${right(c.type) ? "r" : ""}">${esc(c.label)}</th>`).join("")}</tr></thead>
      <tbody>${t.rows.map((r) => `<tr>${t.columns.map((c, i) => `<td class="${right(c.type) ? "r" : ""}">${esc(showCell(r[i], c.type))}</td>`).join("")}</tr>`).join("")}</tbody>
      ${t.totals ? `<tfoot><tr>${t.columns.map((c, i) => `<td class="${right(c.type) ? "r" : ""}">${esc(showCell(t.totals?.[i], c.type))}</td>`).join("")}</tr></tfoot>` : ""}
    </table>`}`).join("");
  const notes = (spec.notes ?? []).map((n) => `<p class="note">${esc(n)}</p>`).join("");

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(spec.title)}</title>
<style>
  @page { size: A4 ${wide ? "landscape" : "portrait"}; margin: 12mm; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { font-family: "Segoe UI", Arial, sans-serif; color: #1c1917; font-size: ${fontSize}px; margin: 0; }
  header { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2px solid #d6d3d1; padding-bottom: 8px; margin-bottom: 12px; }
  .biz { font-size: 11px; color: #78716c; font-weight: 600; }
  h1 { font-size: 18px; margin: 2px 0 0; }
  .meta { text-align: right; color: #57534e; }
  .kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; margin-bottom: 12px; }
  .kpi { border: 1px solid #e7e5e4; border-radius: 6px; padding: 6px 8px; }
  .kl { font-size: 9px; text-transform: uppercase; letter-spacing: .04em; color: #78716c; font-weight: 700; }
  .kv { font-size: 13px; font-weight: 700; margin-top: 2px; }
  h2 { font-size: 13px; margin: 14px 0 6px; }
  table { width: 100%; border-collapse: collapse; }
  th { background: #f5f5f4; text-align: left; font-size: ${Math.max(fontSize - 1, 7.5)}px; color: #57534e; }
  th, td { padding: ${widest > 14 ? "3px 4px" : "4px 6px"}; border-bottom: 1px solid #e7e5e4; vertical-align: top; }
  tr { page-break-inside: avoid; }
  tfoot td { font-weight: 700; border-top: 2px solid #a8a29e; background: #fafaf9; }
  .r { text-align: right; white-space: nowrap; }
  .empty { color: #78716c; }
  .note { color: #57534e; margin: 8px 0 0; }
</style></head><body>
<header>
  <div>${business ? `<div class="biz">${esc(business)}</div>` : ""}<h1>${esc(spec.title)}</h1></div>
  <div class="meta">${spec.period ? `<div>${esc(rangeLabel(spec.period.from, spec.period.to))}</div>` : ""}<div>Printed ${esc(generatedAt())}</div></div>
</header>
${summary ? `<div class="kpis">${summary}</div>` : ""}
${tables}
${notes}
</body></html>`;

  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
  document.body.appendChild(frame);
  const doc = frame.contentDocument;
  const win = frame.contentWindow;
  if (!doc || !win) { frame.remove(); return; }
  doc.open();
  doc.write(html);
  doc.close();
  let removed = false;
  const cleanUp = () => { if (!removed) { removed = true; frame.remove(); } };
  win.onafterprint = () => setTimeout(cleanUp, 100);
  setTimeout(() => {
    win.focus();
    win.print();
    setTimeout(cleanUp, 60000); // in case the browser never reports that printing finished
  }, 150);
}

/**
 * "Excel" and "Print" buttons for a report. `build` turns the data on screen into an
 * ExportSpec (it may fetch first, e.g. every row instead of one page); returning null
 * does nothing.
 */
export function ExportButtons({ build, disabled = false }: {
  build: (kind: "excel" | "print") => ExportSpec | null | undefined | Promise<ExportSpec | null | undefined>;
  disabled?: boolean;
}) {
  const { settings } = useSettings();
  const { showToast } = useToast();
  const business = settings.business?.businessName || "";
  const [busy, setBusy] = useState<"excel" | "print" | null>(null);

  const run = async (kind: "excel" | "print") => {
    setBusy(kind);
    try {
      const spec = await build(kind);
      if (!spec) return;
      if (kind === "excel") await downloadExcel(spec, business);
      else printReport(spec, business);
    } catch (e) {
      showToast(apiErrorMessage(e, kind === "excel" ? "Could not create the Excel file." : "Could not print this report."), "error");
    } finally {
      setBusy(null);
    }
  };

  const btn = "h-10 flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-3 rounded-xl border border-stone-200 bg-white text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed";
  return (
    <div className="flex gap-2 w-full sm:w-auto">
      <button type="button" className={btn} disabled={disabled || busy !== null} onClick={() => run("excel")} title="Download as an Excel file">
        {busy === "excel" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 text-emerald-600" />}
        Excel
      </button>
      <button type="button" className={btn} disabled={disabled || busy !== null} onClick={() => run("print")} title="Print, or save as PDF">
        {busy === "print" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4 text-stone-500" />}
        Print
      </button>
    </div>
  );
}

/** Right-hand side of a report header: date controls and export buttons, stacked on phones. */
export function HeaderTools({ children }: { children: ReactNode }) {
  return <div className="flex flex-col sm:flex-row sm:flex-wrap sm:justify-end gap-2 w-full lg:w-auto">{children}</div>;
}
